import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  NgForm,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import {
  type ConnectorCreationRequest,
  type ConnectorTemplate,
} from '@blaze-shared/connectors-domain';
import { LocalStorageService, StorageKey } from '@blaze-shared/platform-data-access';
import { DialogModule, EnvironmentIndicatorComponent } from '@blaze-shared/shared-feature';
import { ButtonComponent, PanelComponent, PanelRef } from '@blaze-shared/shared-ui';

import { ConnectorsService } from '../../services/connectors.service';

const SHOPIFY_DATASETS = ['Orders', 'Customers', 'Products'];

/** Blaze's public listing on the Shopify app store — the same address in every environment. */
const SHOPIFY_LANDING_PAGE_URL = 'https://apps.shopify.com/blazenocode';

@Component({
  selector: 'blaze-shopify-connector-dialog',
  templateUrl: './shopify-connector-dialog.component.html',
  styleUrls: ['./shopify-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogModule,
    EnvironmentIndicatorComponent,
    ReactiveFormsModule,
    ButtonComponent,
    PanelComponent,
  ],
  host: { class: 'data-connector-dialog' },
})
export class ShopifyConnectorDialogComponent implements OnInit {
  private connectorsService = inject(ConnectorsService);
  private panelRef = inject(PanelRef);
  private localStorageService = inject(LocalStorageService);

  @Input() template?: ConnectorTemplate;
  @Input() connectorCreationRequest?: ConnectorCreationRequest;
  @ViewChild('form') requestAccessForm!: NgForm;

  accountId: string;
  accessForm!: UntypedFormGroup;
  isAfterAuthorization = false;
  shop = '';

  constructor() {
    this.accountId = this.localStorageService.get<string>(StorageKey.AccountId) || '';
  }

  ngOnInit(): void {
    if (this.connectorCreationRequest) {
      this.setAuthorized(this.connectorCreationRequest.payload['shop'] as string);
    }

    this.accessForm = new UntypedFormGroup({
      connectorName: new UntypedFormControl('', Validators.required),
    });
  }

  goToShopify(): void {
    window.open(SHOPIFY_LANDING_PAGE_URL, '_blank');
    this.closeDialog();
  }

  setAuthorized(shop: string): void {
    if (shop) {
      this.isAfterAuthorization = true;
      this.shop = shop;
    }
  }

  closeDialog(): void {
    this.panelRef.close();
  }

  requestAccess(): void {
    if (this.accessForm.invalid) {
      return;
    }

    const { connectorName } = this.accessForm.value;
    const shop = (this.connectorCreationRequest?.payload['shop'] as string) || '';

    const request: any = {
      name: connectorName,
      templateId: this.template?.id,
      accountId: this.accountId,
      configuration: {
        shopUrl: shop,
      },
      actions: [],
      datasets: SHOPIFY_DATASETS.map((name) => ({ name })),
    };

    this.connectorsService
      .createConnector(request, this.connectorCreationRequest?.projectId)
      .subscribe(() => {
        this.closeDialog();
      });
  }
}
